/**
 * A shared book as the read view shows it (part 38c; decisions 75, 76; table NV): the manifest's chapters in its order,
 * each with the copy held of it, the book's metadata and theme, and the notes as threads (a first note and its
 * replies), on a chapter in the order of its text or in the Notebook. A writer's note's passage comes from the
 * manifest's places first (38e's MP), the note's own range second; a place past the chapter's text is the whole chapter.
 * Pure: it reads what the cache holds.
 */
import type { InviteCopy, InviteManifest } from '@cockatrice/core';
import { metadataFromJson } from '@cockatrice/layout';
import type { BookMetadata } from '@cockatrice/layout';
import type { CachedClaim, HeldNote } from './store.ts';

/** A chapter of the shared book: its ID, title and text. */
export interface SharedChapter {
  readonly id: string;
  readonly title: string;
  readonly text: string;
}

/** The shared book: its metadata (null when it doesn't read), theme, chapters held, those not held, and its notes' places. */
export interface SharedBook {
  readonly metadata: BookMetadata | null;
  readonly theme: string;
  readonly chapters: readonly SharedChapter[];
  /** Chapters the manifest names whose copy isn't held (not downloaded yet, or not opening: CC7). */
  readonly missing: number;
  readonly places: ReadonlyMap<string, { readonly from: number; readonly to: number } | null>;
}

/** Who wrote a note, as the view says it (IN9: within a group, the key doesn't say which member). */
export type Who = { readonly kind: 'you' } | { readonly kind: 'writer' } | { readonly kind: 'reader'; readonly group: HeldNote['group'] };

/** A thread: its first note, its passage (null: the whole chapter, or the Notebook), and its replies, oldest first. */
export interface Thread {
  readonly first: HeldNote;
  readonly range: { readonly from: number; readonly to: number } | null;
  readonly replies: readonly HeldNote[];
}

/** The manifest among `items`, by its item `manifest`; null without one. */
function manifestIn(items: ReadonlyMap<string, InviteCopy>, manifest: string): InviteManifest | null {
  const copy = items.get(manifest);
  return copy?.kind === 'manifest' ? copy : null;
}

/** The book `items` hold (opened by `Shares.open`) with the claim's manifest item `manifest`; null without a manifest. */
export function bookOf(items: ReadonlyMap<string, InviteCopy>, manifest: string): SharedBook | null {
  const found = manifestIn(items, manifest);
  if (found === null) return null;
  let metadata: BookMetadata | null;
  try {
    metadata = metadataFromJson(found.metadata);
  } catch {
    metadata = null;
  }
  const chapters: SharedChapter[] = [];
  for (const id of found.chapters) {
    const copy = items.get(id);
    if (copy?.kind === 'chapter') chapters.push({ id, title: copy.title, text: copy.text });
  }
  return {
    metadata,
    theme: found.theme,
    chapters,
    missing: found.chapters.length - chapters.length,
    places: new Map(found.places.map((place) => [place.note, place.range])),
  };
}

/** Who wrote `note` for the claim `invite`. */
export function whoWrote(note: HeldNote, invite: string): Who {
  if (note.author === null) return { kind: 'writer' };
  return note.author === invite ? { kind: 'you' } : { kind: 'reader', group: note.group };
}

/** Whether `note` is the claim `invite`'s own (decision 76: only its own may be edited or deleted). */
export function isOwn(note: HeldNote, invite: string): boolean {
  return note.author === invite;
}

/**
 * NV, 38e's MP: the passage of `first` on a chapter of `length` visible units: the manifest's place for a writer's note
 * it names (null there: the passage is gone), else the note's own range; past the text, the whole chapter.
 */
export function passageOf(first: HeldNote, book: Pick<SharedBook, 'places'>, length: number): { readonly from: number; readonly to: number } | null {
  if (first.note.kind !== 'note') return null;
  const named = first.author === null && book.places.has(first.id);
  const range = named ? (book.places.get(first.id) ?? null) : (first.note.anchor?.range ?? null);
  return range === null || range.to > length ? null : range;
}

/**
 * NV: the threads of `claim` on chapter `chapter` (null: the Notebook), in the text's order (a passage by its start,
 * then the whole chapter's, each by when it was written), with `length` the chapter's visible units. A reply whose first
 * note isn't held isn't shown (as 19e's fix-next 44 leaves it).
 */
export function threadsOf(claim: Pick<CachedClaim, 'held'>, book: Pick<SharedBook, 'places'>, chapter: string | null, length: number): readonly Thread[] {
  const replies = new Map<string, HeldNote[]>();
  for (const note of claim.held) {
    if (note.note.kind !== 'reply') continue;
    const list = replies.get(note.note.to) ?? [];
    list.push(note);
    replies.set(note.note.to, list);
  }
  const threads = claim.held.flatMap((note): Thread[] => (note.note.kind === 'note' && note.note.chapter === chapter ? [{ first: note, range: chapter === null ? null : passageOf(note, book, length), replies: replies.get(note.id) ?? [] }] : []));
  const start = (thread: Thread): number => thread.range?.from ?? Number.MAX_SAFE_INTEGER;
  return threads.sort((a, b) => start(a) - start(b) || a.first.at - b.first.at || a.first.seq - b.first.seq);
}

/** NV: how many threads each chapter has, by chapter ID (the rail's counts). */
export function countsOf(claim: Pick<CachedClaim, 'held'>): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const note of claim.held) {
    if (note.note.kind === 'note' && note.note.chapter !== null) counts.set(note.note.chapter, (counts.get(note.note.chapter) ?? 0) + 1);
  }
  return counts;
}
